import {
  AccountApi,
  AuthenticationApi,
  CreateUser,
  ErrorResponse,
  Member,
  MemberList,
  MembersApi,
  Program,
  SuccessResponse,
  User,
} from '@citruscamps/citrus-client'
import { IANAZone, Settings } from 'luxon'
import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { ProvideFlag, useProvideFlags } from '../components/FeatureFlag/hooks/useFlags'
import translations from '../translations/en.json'
import { generateApiConfig } from '../utils/client-config'
import { generateBaseKey } from '../utils/key-generator'
import { toQueryParams } from '../utils/query-params'
import { useLocalStorage } from './useLocalStorage'
import { useRequestHandler } from './useRequestHandler'
import { useRouter } from './useRouter'
import { useToast } from './useToast'
import { useWebPushNotifications } from './useWebPushNotifications'

const DefaultContext: AuthValue = {
  isLoading: true,
  isError: false,
  isSwitching: true,
  handleLogin: async () => {
    throw new Error('Method not initialized.')
  },
  handleSignUp: async () => {
    throw new Error('Method not initialized.')
  },
  handleUpdate: async () => {
    throw new Error('Method not initialized.')
  },
  setProgramId: async () => {
    throw new Error('Method not initialized.')
  },
  handleNotificationTest: () => {
    throw new Error('Method not initialized.')
  },
  setUser: () => {
    throw new Error('Method not initialized.')
  },
}

const authContext: React.Context<AuthValue> = createContext<AuthValue>(DefaultContext)

// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAuth().

export const ProvideAuth = ({
  programId: routeId,
  children,
}: React.PropsWithChildren<IProps>): React.ReactElement => {
  const auth: AuthValue = useProvideAuth({ programId: routeId })
  return (
    <authContext.Provider value={auth}>
      <ProvideFlag programId={auth.programId} user={auth.user}>
        {children}
      </ProvideFlag>
    </authContext.Provider>
  )
}

// Hook for child components to get the auth object ...
// ... and re-render when it changes.

export const useAuth = (): AuthValue => {
  return useContext<AuthValue>(authContext)
}

// Provider hook that creates auth object and handles state

export interface AuthValue {
  user?: User
  member?: Member
  program?: Program
  programId?: string
  error?: ErrorResponse
  isLoading: boolean
  isError: boolean
  isSwitching: boolean
  notificationPermission?: NotificationPermission
  handleLogin: (email: string, password: string) => Promise<void>
  handleSignUp: (values: CreateUser) => Promise<User>
  handleUpdate: (values: Member) => Promise<void>
  handleNotificationTest: () => Promise<void>
  setProgramId: React.Dispatch<React.SetStateAction<string | undefined>>
  setUser: (user: User | undefined) => Promise<void>
}

interface IProps {
  programId?: string
}

export const useProvideAuth = ({ programId: initialProgramId }: IProps = {}): AuthValue => {
  const { requestHandler } = useRequestHandler()
  const { setToast } = useToast()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [programId, _setProgramId] = useLocalStorage<string | undefined>(
    'citrus_context',
    initialProgramId,
  )
  const timeoutRef = useRef<number>(0)
  const [isSwitching, setIsSwitching] = useState<boolean>(false)
  // switching is too fast
  const setProgramId = (action: React.SetStateAction<string | undefined>) => {
    setIsSwitching(true)
    _setProgramId(action)
    if (router.search['program_id']) {
      delete router.search['program_id']
      router.navigate(
        {
          pathname: router.pathname,
          search: toQueryParams(router.search),
        },
        { replace: true },
      )
    }
    window.setTimeout(() => {
      setIsSwitching(false)
    }, 300)
  }
  const {
    data: [user, error] = [],
    isError,
    isLoading,
  } = useQuery<[User | undefined, ErrorResponse | undefined]>('user', async ({ signal }) => {
    try {
      const accountClient = new AccountApi(generateApiConfig())
      const _user = await requestHandler<User>(
        () => accountClient.findByCurrentUserAccount({ signal }),
        {
          redirect: false,
        },
      )
      return [_user, undefined]
    } catch (e) {
      return [undefined, e as ErrorResponse]
    }
  })

  const { data: member, isLoading: isLoadingMember } = useQuery<Member, ErrorResponse>(
    generateBaseKey({
      type: 'member',
      program_id: (initialProgramId || programId) as any,
    }),
    async () => {
      const programAccountClient = new AccountApi(generateApiConfig())
      const { data } = await requestHandler<MemberList>(() =>
        programAccountClient.findMembersByUserProgramAccount({
          limit: 1,
          program_id: initialProgramId || programId || undefined,
        }),
      )
      const active: Member = data?.[0]
      if (active?.program?.id !== programId) {
        setProgramId(active?.program?.id)
      }
      return active
    },
    { enabled: !!user && !isLoading },
  )
  const program: Program | undefined = isSwitching ? undefined : member?.program
  const { flags, isLoading: isLoadingFlags } = useProvideFlags({ user, programId })
  const { permission: notificationPermission, handleTest: handleNotificationTest } =
    useWebPushNotifications({ user, features: flags })
  if (program?.billing?.trial_start) {
    program.billing.trial_start = new Date(program?.billing?.trial_start)
  }
  if (program?.billing?.trial_end) {
    program.billing.trial_end = new Date(program?.billing?.trial_end)
  }

  Settings.defaultZone = IANAZone.create(
    member?.timezone ||
      member?.program?.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone,
  )

  const handleLogin = async (email: string, password: string): Promise<void> => {
    const authClient = new AuthenticationApi(generateApiConfig())
    await requestHandler<SuccessResponse>(
      () => authClient.loginAuth({ Login: { username: email, password } }),
      {
        retry: false,
      },
    )
    await queryClient.refetchQueries('user')
  }

  const handleSignUp = async (createUser: CreateUser): Promise<User> => {
    const accountClient = new AccountApi(generateApiConfig())
    return await requestHandler<User>(() => accountClient.signupAccount({ CreateUser: createUser }))
  }

  const handleUpdate = async (values: Member): Promise<void> => {
    const memberClient = new MembersApi(generateApiConfig())
    try {
      await requestHandler<Member>(() =>
        memberClient.updateMember({
          program_id: values.program.id,
          member_id: values.id,
          Member: values,
        }),
      )
      await queryClient.refetchQueries('user')
    } catch (e: any) {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: e?.message || (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      throw e
    }
  }

  const setUser = async (user: User | undefined) => {
    await queryClient.setQueryData('user', [user, undefined])
  }

  useEffect(() => {
    const _ref = timeoutRef.current
    return () => window.clearTimeout(_ref)
  }, [])

  return {
    user,
    member,
    program,
    programId,
    error: error || undefined,
    isLoading,
    isError,
    isSwitching: isSwitching || isLoadingMember || isLoadingFlags,
    notificationPermission,
    handleLogin,
    handleSignUp,
    handleUpdate,
    handleNotificationTest,
    setProgramId,
    setUser,
  }
}
